import { createClient } from '@supabase/supabase-js'

// ─── Replace these with your Supabase project values ───────────────────────
// Found in: Supabase Dashboard → Settings → API
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://YOUR_PROJECT.supabase.co'
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'YOUR_ANON_KEY'
// ───────────────────────────────────────────────────────────────────────────

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// ── Auth helpers ────────────────────────────────────────────────────────────
export const signIn = (email, password) =>
  supabase.auth.signInWithPassword({ email, password })

export const signUp = (email, password, meta) =>
  supabase.auth.signUp({ email, password, options: { data: meta } })

export const signOut = () => supabase.auth.signOut()

export const getSession = () => supabase.auth.getSession()

// ── Profile ─────────────────────────────────────────────────────────────────
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
  return { data, error }
}

// ── Instructors ─────────────────────────────────────────────────────────────
export const getInstructors = async () => {
  const { data, error } = await supabase
    .from('instructors')
    .select(`
      *,
      profile:profiles(id, full_name, email, phone, avatar_url)
    `)
    .eq('is_active', true)
    .order('rating', { ascending: false })
  return { data, error }
}

export const getInstructorByProfile = async (profileId) => {
  const { data, error } = await supabase
    .from('instructors')
    .select(`*, profile:profiles(*)`)
    .eq('profile_id', profileId)
    .single()
  return { data, error }
}

// ── Availability ────────────────────────────────────────────────────────────
export const getAvailability = async (instructorId) => {
  const { data, error } = await supabase
    .from('availability')
    .select('*')
    .eq('instructor_id', instructorId)
  return { data, error }
}

export const upsertAvailability = async (rows) => {
  const { data, error } = await supabase
    .from('availability')
    .upsert(rows)
    .select()
  return { data, error }
}

// ── Lessons ─────────────────────────────────────────────────────────────────
export const getLessons = async (filters = {}) => {
  let query = supabase
    .from('lessons')
    .select(`
      *,
      customer:profiles!lessons_customer_id_fkey(id, full_name, email, phone),
      instructor:instructors(
        id, rating, specialties, hourly_rate,
        profile:profiles(id, full_name, avatar_url)
      )
    `)
    .order('scheduled_date', { ascending: true })
    .order('start_time', { ascending: true })

  if (filters.date) query = query.eq('scheduled_date', filters.date)
  if (filters.week_start && filters.week_end) {
    query = query.gte('scheduled_date', filters.week_start).lte('scheduled_date', filters.week_end)
  }
  if (filters.instructor_id) query = query.eq('instructor_id', filters.instructor_id)
  if (filters.customer_id) query = query.eq('customer_id', filters.customer_id)
  if (filters.status) query = query.eq('status', filters.status)

  const { data, error } = await query
  return { data, error }
}

export const createLesson = async (lesson) => {
  const { data, error } = await supabase
    .from('lessons')
    .insert(lesson)
    .select()
    .single()
  return { data, error }
}

export const updateLesson = async (id, updates) => {
  const { data, error } = await supabase
    .from('lessons')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

export const assignInstructor = async (lessonId, instructorId) => {
  const result = await updateLesson(lessonId, { instructor_id: instructorId, status: 'confirmed' })
  // Clear all pending requests for this lesson now that it's assigned
  await supabase
    .from('lesson_requests')
    .update({ status: 'cleared' })
    .eq('lesson_id', lessonId)
    .eq('status', 'pending')
  return result
}

export const requestLesson = async (lessonId, instructorId) => {
  return supabase
    .from('lesson_requests')
    .upsert(
      { lesson_id: lessonId, instructor_id: instructorId, status: 'pending' },
      { onConflict: 'lesson_id,instructor_id' }
    )
}

export const withdrawLessonRequest = async (lessonId, instructorId) => {
  return supabase
    .from('lesson_requests')
    .update({ status: 'withdrawn' })
    .eq('lesson_id', lessonId)
    .eq('instructor_id', instructorId)
}

export const getLessonRequests = async (lessonId) => {
  return supabase
    .from('lesson_requests')
    .select(`
      instructor_id,
      requested_at:created_at,
      status,
      instructor:instructors(id, rating, specialties, profile:profiles(full_name))
    `)
    .eq('lesson_id', lessonId)
    .eq('status', 'pending')
}

// ── AI Matching ─────────────────────────────────────────────────────────────
export const getAIMatchedInstructors = async (lesson) => {
  // Fetch all active instructors
  const { data: instructors } = await getInstructors()
  if (!instructors) return []

  // Fetch existing lessons on that date to check double-booking
  const { data: existing } = await getLessons({ date: lesson.scheduled_date })

  const scored = instructors.map(inst => {
    let score = 50

    // Specialty match
    if (inst.specialties?.includes(lesson.lesson_type)) score += 20
    if (inst.specialties?.includes(lesson.skill_level)) score += 10

    // Location match
    if (inst.locations?.includes(lesson.location)) score += 15

    // Rating boost
    score += (inst.rating - 3) * 5

    // Penalize if double-booked
    const conflict = existing?.some(l =>
      l.instructor_id === inst.id &&
      l.id !== lesson.id &&
      l.start_time < lesson.end_time &&
      l.end_time > lesson.start_time
    )
    if (conflict) score = -999

    return {
      ...inst,
      match_score: Math.max(0, Math.min(100, Math.round(score))),
      has_conflict: conflict
    }
  })

  return scored
    .filter(i => !i.has_conflict)
    .sort((a, b) => b.match_score - a.match_score)
}

// ── Time Off ────────────────────────────────────────────────────────────────
export const getTimeOffRequests = async (instructorId) => {
  const { data, error } = await supabase
    .from('time_off_requests')
    .select('*')
    .eq('instructor_id', instructorId)
    .order('created_at', { ascending: false })
  return { data, error }
}

export const createTimeOffRequest = async (req) => {
  const { data, error } = await supabase
    .from('time_off_requests')
    .insert(req)
    .select()
    .single()
  return { data, error }
}

export const updateTimeOffRequest = async (id, status) => {
  const { data, error } = await supabase
    .from('time_off_requests')
    .update({ status })
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

// ── Clock In/Out ─────────────────────────────────────────────────────────────
export const clockIn = async (instructorId, lessonId) => {
  const { data, error } = await supabase
    .from('time_logs')
    .insert({ instructor_id: instructorId, lesson_id: lessonId, clocked_in_at: new Date().toISOString() })
    .select()
    .single()
  return { data, error }
}

export const clockOut = async (logId) => {
  const { data, error } = await supabase
    .from('time_logs')
    .update({ clocked_out_at: new Date().toISOString() })
    .eq('id', logId)
    .select()
    .single()
  return { data, error }
}

export const getWeeklyHours = async (instructorId, weekStart, weekEnd) => {
  const { data, error } = await supabase
    .from('time_logs')
    .select('total_minutes, lesson:lessons(scheduled_date)')
    .eq('instructor_id', instructorId)
    .gte('clocked_in_at', weekStart)
    .lte('clocked_in_at', weekEnd)
  return { data, error }
}
